/**
 * Pure visual projections for {@link Cblurbackdrop}.
 *
 * The expensive properties — `backdrop-filter` and the composed background —
 * are projected ONCE and stay constant for the layer's whole life. Only the
 * parent's opacity animates, so the fade runs on the compositor and the browser
 * can reuse a single backdrop raster instead of re-sampling and re-blurring the
 * full screen on every frame of the modal's enter/exit.
 */

import type { CSSProperties } from 'react';
import { ColorType, getColorValue } from '@/theme/colors';

const NO_BACKDROP_BLUR = 'blur(0px)';
const NO_BACKGROUND_IMAGE = 'none';
const TRANSPARENT_BLACK = 'rgba(0,0,0,0)';
const BACKDROP_LAYER_KEY_SEPARATOR = '|';

/** Layer-key tokens. Closed set — they only ever appear inside the layer key. */
enum BackdropLayerKeyToken {
  Gradient = 'g',
  Solid = 's',
  AutoZIndex = 'auto',
}

/**
 * How the backdrop's single animated channel (parent opacity) is shaped.
 * The expensive properties are static in every variant — this axis only chooses
 * the dismissal/entrance envelope.
 */
export enum BackdropFadeVariant {
  /**
   * Reproduces the pre-static-blur composite exactly. That implementation faded
   * parent opacity AND the colour/gradient alpha together on exit, so apparent
   * alpha followed `opacity * (1 - E(t))²`. With opacity as the only channel the
   * equivalent exit easing is `E'(t) = 1 - (1 - E(t))²`. Entrance already matched
   * (`opacity * E(t)`), so only the exit curve is reshaped.
   */
  Matched = 'matched',
  /** Plain opacity fade both ways — reads heavier late in a dismissal. */
  Linear = 'linear',
  /** Longer, gentler tail on the way out. */
  Soft = 'soft',
}

type BackdropEasing = (progress: number) => number;

const CUBIC_EASE_IN_OUT: BackdropEasing = (progress) =>
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

/** `1 - (1 - E(t))²` — the double-attenuation envelope, as one easing function. */
const MATCHED_EXIT_EASING: BackdropEasing = (progress) => {
  const remaining = 1 - CUBIC_EASE_IN_OUT(progress);
  return 1 - remaining * remaining;
};

const SOFT_EXIT_EASING: BackdropEasing = (progress) =>
  1 - Math.pow(1 - progress, 3);

const BACKDROP_EXIT_EASING: Record<BackdropFadeVariant, BackdropEasing | null> = {
  [BackdropFadeVariant.Matched]: MATCHED_EXIT_EASING,
  [BackdropFadeVariant.Linear]: null,
  [BackdropFadeVariant.Soft]: SOFT_EXIT_EASING,
};

/**
 * The exit easing for a variant, or `null` to keep the caller's default ease.
 * Entrance is deliberately never overridden — it already matches the legacy
 * composite in every variant.
 */
export function getBackdropExitEasing(
  variant: BackdropFadeVariant,
): BackdropEasing | null {
  return BACKDROP_EXIT_EASING[variant];
}

export interface GetBackdropLayerKeyInput {
  blur: number;
  opacity: number;
  topOffset: number;
  useGradient: boolean;
  zIndexOverride: number | undefined;
}

/**
 * Layer identity. Every value here is baked STATICALLY into a layer, so a change
 * to any of them must mount a new layer and crossfade rather than mutate the
 * live one — mutating it would snap the full-screen blur in a single frame.
 */
export function getBackdropLayerKey({
  blur,
  opacity,
  topOffset,
  useGradient,
  zIndexOverride,
}: GetBackdropLayerKeyInput): string {
  return [
    blur,
    opacity,
    topOffset,
    useGradient
      ? BackdropLayerKeyToken.Gradient
      : BackdropLayerKeyToken.Solid,
    zIndexOverride ?? BackdropLayerKeyToken.AutoZIndex,
  ].join(BACKDROP_LAYER_KEY_SEPARATOR);
}

interface GetBackdropColorInput {
  opacity: number;
  useGradient: boolean;
}

function getBackdropColor({
  opacity,
  useGradient,
}: GetBackdropColorInput): string {
  return useGradient
    ? TRANSPARENT_BLACK
    : getColorValue(ColorType.Black, opacity);
}

function getBackdropFilter(blur: number): string {
  return blur <= 0 ? NO_BACKDROP_BLUR : `blur(${blur}px)`;
}

interface GetBackdropVisualStyleInput {
  blur: number;
  opacity: number;
  useGradient: boolean;
}

export function getBackdropVisualStyle({
  blur,
  opacity,
  useGradient,
}: GetBackdropVisualStyleInput): CSSProperties {
  const backdropFilter = getBackdropFilter(blur);

  return {
    backdropFilter,
    WebkitBackdropFilter: backdropFilter,
    backgroundColor: getBackdropColor({ useGradient, opacity }),
  };
}

export interface GetBackdropParentStyleInput
  extends GetBackdropVisualStyleInput {
  backdropStyle: CSSProperties;
}

/**
 * Gradient mode clears the caller-installed background image because the
 * gradient moves to its own child layer; solid mode keeps it beneath the
 * alpha-baked black `backgroundColor`.
 */
export function getBackdropParentStyle({
  backdropStyle,
  blur,
  opacity,
  useGradient,
}: GetBackdropParentStyleInput): CSSProperties {
  return {
    ...backdropStyle,
    backgroundImage: useGradient ? NO_BACKGROUND_IMAGE : backdropStyle.backgroundImage,
    ...getBackdropVisualStyle({ blur, opacity, useGradient }),
  };
}

export interface GetBackdropGradientStyleInput {
  gradient: string;
  opacity: number;
}

export function getBackdropGradientStyle({
  gradient,
  opacity,
}: GetBackdropGradientStyleInput): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    backgroundImage: gradient,
    opacity,
    zIndex: -1,
  };
}
